import { useQuery } from '@tanstack/react-query';
import { Users, Mail, Phone, Building2, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { getAnnouncementContacts } from '../lib/api/index.js';
import type { ExtractedContact } from '../lib/api/types.js';

interface AnnouncementContactsCardProps {
  announcementId: string;
}

const roleLabels: Record<string, string> = {
  media_contact: 'Media Contact',
  pr_representative: 'PR Representative',
  investor_relations: 'Investor Relations',
  analyst: 'Analyst',
  executive: 'Executive',
  board_member: 'Board Member',
  advisor: 'Advisor',
};

const contactTypeLabels: Record<string, string> = {
  internal: 'Internal',
  external_pr: 'External PR',
  external_media: 'External Media',
  external_analyst: 'External Analyst',
  external_advisor: 'External Advisor',
};

const contactTypeColors: Record<string, string> = {
  internal: 'bg-blue-100 text-blue-700',
  external_pr: 'bg-purple-100 text-purple-700',
  external_media: 'bg-orange-100 text-orange-700',
  external_analyst: 'bg-green-100 text-green-700',
  external_advisor: 'bg-grey-100 text-grey-700',
};

const syncStatusIcons: Record<string, React.ReactNode> = {
  pending: <Clock className="h-4 w-4 text-yellow-600" />,
  synced: <CheckCircle className="h-4 w-4 text-green-600" />,
  failed: <XCircle className="h-4 w-4 text-red-600" />,
  skipped: <AlertTriangle className="h-4 w-4 text-grey-500" />,
};

const syncStatusLabels: Record<string, string> = {
  pending: 'Awaiting Cynapse sync',
  synced: 'Synced to Cynapse',
  failed: 'Cynapse sync failed',
  skipped: 'Generic contact (not synced)',
};

export function AnnouncementContactsCard({ announcementId }: AnnouncementContactsCardProps) {
  const { data: extraction, isLoading, error } = useQuery({
    queryKey: ['announcement-contacts', announcementId],
    queryFn: () => getAnnouncementContacts(announcementId),
    enabled: !!announcementId,
  });

  if (isLoading) {
    return (
      <div className="bg-surface rounded-xl border border-grey-200 shadow-md p-8">
        <div className="flex items-center space-x-3 mb-6">
          <Users className="h-6 w-6 text-grey-400" />
          <h2 className="text-xl font-bold text-grey-900">Extracted Contacts</h2>
        </div>
        <div className="text-center py-8">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent"></div>
          <p className="mt-4 text-grey-600">Loading contacts...</p>
        </div>
      </div>
    );
  }

  // Show empty state if no contacts were extracted
  if (error || !extraction) {
    return (
      <div className="bg-surface rounded-xl border border-grey-200 shadow-md p-8">
        <div className="flex items-center space-x-3 mb-6">
          <Users className="h-6 w-6 text-grey-400" />
          <h2 className="text-xl font-bold text-grey-900">Extracted Contacts</h2>
        </div>
        <div className="text-center py-12">
          <Users className="h-16 w-16 text-grey-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-grey-700 mb-2">No Contacts Extracted</h3>
          <p className="text-grey-500 max-w-md mx-auto">
            No contact information was found in this announcement. Contacts will appear here once extracted from the PDF.
          </p>
        </div>
      </div>
    );
  }

  const personalContacts = extraction.contacts.filter((c: ExtractedContact) => !c.is_generic_contact);
  const genericContacts = extraction.contacts.filter((c: ExtractedContact) => c.is_generic_contact);

  return (
    <div className="bg-surface rounded-xl border border-grey-200 shadow-md p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Users className="h-6 w-6 text-primary-600" />
          <h2 className="text-xl font-bold text-grey-900">Extracted Contacts</h2>
        </div>
        <div className="flex items-center space-x-2 text-sm">
          {syncStatusIcons[extraction.cynapse_sync_status]}
          <span className="text-grey-600">{syncStatusLabels[extraction.cynapse_sync_status]}</span>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-grey-50 rounded-lg">
        <div>
          <p className="text-sm text-grey-500 font-medium mb-1">Total Contacts</p>
          <p className="text-2xl font-bold text-grey-900">{extraction.contact_count}</p>
        </div>
        <div>
          <p className="text-sm text-grey-500 font-medium mb-1">Personal Contacts</p>
          <p className="text-2xl font-bold text-primary-600">{personalContacts.length}</p>
        </div>
        <div>
          <p className="text-sm text-grey-500 font-medium mb-1">Generic Contacts</p>
          <p className="text-2xl font-bold text-grey-500">{genericContacts.length}</p>
        </div>
      </div>

      {/* Personal Contacts */}
      {personalContacts.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-grey-900 mb-4">Personal Contacts</h3>
          <div className="space-y-4">
            {personalContacts.map((contact: ExtractedContact, index: number) => (
              <ContactCard key={index} contact={contact} />
            ))}
          </div>
        </div>
      )}

      {/* Generic Contacts */}
      {genericContacts.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-grey-500 mb-4">Generic Contacts</h3>
          <div className="space-y-4">
            {genericContacts.map((contact: ExtractedContact, index: number) => (
              <ContactCard key={index} contact={contact} isGeneric />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ContactCard({ contact, isGeneric = false }: { contact: ExtractedContact; isGeneric?: boolean }) {
  return (
    <div className={`p-4 border rounded-lg ${isGeneric ? 'border-grey-200 bg-grey-50' : 'border-primary-200 bg-primary-50'}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className={`font-semibold ${isGeneric ? 'text-grey-700' : 'text-grey-900'}`}>
            {contact.full_name}
          </h4>
          {contact.job_title && (
            <p className="text-sm text-grey-600 mt-1">{contact.job_title}</p>
          )}
          {contact.organization && (
            <div className="flex items-center space-x-1.5 text-sm text-grey-500 mt-1">
              <Building2 className="h-3.5 w-3.5" />
              <span>{contact.organization}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end space-y-2">
          <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${contactTypeColors[contact.contact_type] || 'bg-grey-100 text-grey-600'}`}>
            {contactTypeLabels[contact.contact_type]}
          </span>
          <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-grey-100 text-grey-600">
            {roleLabels[contact.role_in_announcement]}
          </span>
          {isGeneric && (
            <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-yellow-100 text-yellow-700">
              Generic
            </span>
          )}
        </div>
      </div>

      {/* Contact Details */}
      <div className="space-y-2">
        {contact.email_addresses.map((email, index) => (
          <div key={index} className="flex items-center space-x-2 text-sm">
            <Mail className="h-4 w-4 text-grey-400" />
            <a href={`mailto:${email.email}`} className="text-primary-600 hover:text-primary-700">
              {email.email}
            </a>
            {email.type === 'generic' && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700">generic</span>
            )}
          </div>
        ))}
        {contact.phone_numbers.map((phone, index) => (
          <div key={index} className="flex items-center space-x-2 text-sm">
            <Phone className="h-4 w-4 text-grey-400" />
            <a href={`tel:${phone.number}`} className="text-primary-600 hover:text-primary-700">
              {phone.number}
            </a>
            <span className="text-xs text-grey-500">({phone.type})</span>
          </div>
        ))}
        {contact.email_addresses.length === 0 && contact.phone_numbers.length === 0 && (
          <div className="flex items-center space-x-2 text-sm text-grey-500 italic">
            <Mail className="h-4 w-4 text-grey-400" />
            <span>No contact details available - candidate for Cynapse enrichment</span>
          </div>
        )}
      </div>

      {/* Confidence Score */}
      <div className="mt-3 pt-3 border-t border-grey-200 flex items-center justify-between">
        <span className="text-xs text-grey-500">Confidence: {(contact.confidence * 100).toFixed(0)}%</span>
        {contact.context_snippet && (
          <button
            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            onClick={() => alert(contact.context_snippet)}
            title="View source context"
          >
            View Context
          </button>
        )}
      </div>
    </div>
  );
}
